import React, { useState, useCallback } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  MinusCircleIcon,
  FunnelIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { getPosComparison } from '../api/client';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtVES(val) {
  if (val === null || val === undefined) return '—';
  return `Bs ${Math.abs(val).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function sevenDaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  ok: {
    label: 'Cuadra',
    color: 'bg-green-100 text-green-800',
    Icon: CheckCircleIcon,
    iconColor: 'text-green-500',
  },
  parcial: {
    label: 'Parcial',
    color: 'bg-yellow-100 text-yellow-800',
    Icon: ExclamationTriangleIcon,
    iconColor: 'text-yellow-500',
  },
  diferencia: {
    label: 'Diferencia',
    color: 'bg-red-100 text-red-800',
    Icon: XCircleIcon,
    iconColor: 'text-red-500',
  },
  sin_credito: {
    label: 'Sin crédito',
    color: 'bg-gray-100 text-gray-600',
    Icon: MinusCircleIcon,
    iconColor: 'text-gray-400',
  },
  pendiente: {
    label: 'Pendiente',
    color: 'bg-sky-100 text-sky-800',
    Icon: MinusCircleIcon,
    iconColor: 'text-sky-500',
  },
  sin_lote_pos: {
    label: 'Sin lote en POS',
    color: 'bg-orange-100 text-orange-800',
    Icon: ExclamationTriangleIcon,
    iconColor: 'text-orange-500',
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.sin_credito;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon className={`w-3.5 h-3.5 ${cfg.iconColor}`} />
      {cfg.label}
    </span>
  );
}

// ─── Summary cards ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, colorClass }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${colorClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-0.5">{label}</p>
      <p className="text-xl font-black">{value}</p>
      {sub && <p className="text-xs opacity-50 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Bank color dot ───────────────────────────────────────────────────────────

const BANK_COLORS = {
  bdv:      'bg-red-500',
  bnc:      'bg-yellow-500',
  banesco:  'bg-purple-500',
  banplus:  'bg-blue-600',
};

function BankDot({ banco }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${BANK_COLORS[banco] || 'bg-gray-400'}`} />
      <span className="font-semibold text-sm">{banco?.toUpperCase()}</span>
    </span>
  );
}

// ─── Detalle línea a línea: cada lote con sus líneas del banco debajo ─────────

const LINE_STATUS = {
  ok:            { dot: 'bg-green-500',  text: 'text-green-700',  label: 'Cuadra' },
  ok_neto:       { dot: 'bg-green-500',  text: 'text-green-700',  label: 'Cuadra neto' },
  aprox:         { dot: 'bg-yellow-500', text: 'text-yellow-700', label: 'Aprox.' },
  monto_difiere: { dot: 'bg-red-500',    text: 'text-red-700',    label: 'Monto difiere' },
  sin_credito:   { dot: 'bg-gray-300',   text: 'text-gray-400',   label: 'Sin crédito' },
};

function LineDetail({ row }) {
  const lotes   = row.lotes || [];
  const orphans = row.orphan_movements || [];

  return (
    <tr className="bg-gray-50/70">
      <td colSpan={8} className="px-6 py-4">
        <div className="max-w-4xl">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
              Conciliación lote por lote · {row.banco_display}
            </p>
            <p className="text-xs text-gray-400">
              {row.lotes_matched}/{row.lotes_total} cuadran
              {row.lotes_diff > 0 && <span className="text-red-600 font-semibold"> · {row.lotes_diff} con monto distinto</span>}
              {row.duplicates_ignored > 0 && <span> · {row.duplicates_ignored} líneas duplicadas ignoradas</span>}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {lotes.map(l => {
              const s = LINE_STATUS[l.status] || LINE_STATUS.sin_credito;
              const diff = l.matched_amount != null ? l.matched_amount - l.amount_ves : null;
              return (
                <div key={l.lote_id}>
                  {/* Lote (POS) */}
                  <div className="px-3 py-2 flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
                    <span className="text-[10px] font-bold uppercase text-gray-400 w-8">POS</span>
                    <span className="font-mono text-sm font-bold text-gray-900 w-24">Lote {l.numerolote || '—'}</span>
                    <span className="text-xs text-gray-400 w-12">{l.hora}</span>
                    <span className="font-mono text-sm font-bold text-gray-900 ml-auto tabular-nums">{fmtVES(l.amount_ves)}</span>
                    {l.suspicious && (
                      <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold"
                            title="Monto muy bajo: la cajera registró cantidad en vez de total">⚠ dato POS</span>
                    )}
                    <span className={`text-xs font-semibold w-32 text-right ${s.text}`}>
                      {s.label}
                      {l.fee_pct > 0 && (l.status === 'ok_neto' || l.status === 'aprox') && (
                        <span className="block text-[10px] font-normal">comisión −{l.fee_pct.toLocaleString('es-VE')}%</span>
                      )}
                      {l.banco_real && (
                        <span className="block text-[10px] font-normal text-orange-600" title="El POS tiene este terminal asignado a otro banco">
                          acreditó {l.banco_real.toUpperCase()}
                        </span>
                      )}
                      {l.numero_banco && (
                        <span className="block text-[10px] font-normal text-orange-600" title="El número de lote anotado en el POS no coincide con el que reporta el banco">
                          banco: lote {l.numero_banco}
                        </span>
                      )}
                      {diff != null && Math.abs(diff) > 1 && l.status === 'monto_difiere' && (
                        <span className="block text-[10px] font-normal">
                          {diff > 0 ? '+' : '−'}{fmtVES(Math.abs(diff))}
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Líneas del banco para este lote */}
                  {(l.matched_lines || []).map(m => (
                    <div key={m.movement_id} className="px-3 py-1.5 pl-10 flex items-center gap-3 bg-gray-50/60">
                      <span className="text-[10px] font-bold uppercase text-gray-400 w-8">Banco</span>
                      <span className="text-xs text-gray-500 w-12">{m.date?.slice(5)}</span>
                      <span className="text-xs text-gray-600 truncate flex-1" title={`${m.description} · Ref ${m.reference}`}>
                        {m.description}
                      </span>
                      <span className="font-mono text-sm text-gray-800 tabular-nums">{fmtVES(m.amount_ves)}</span>
                      <span className="w-32" />
                    </div>
                  ))}
                  {l.status === 'sin_credito' && (
                    <div className="px-3 py-1.5 pl-10 text-xs text-gray-400 italic bg-gray-50/60">
                      {row.status === 'pendiente'
                        ? `Se acredita el ${row.credit_date?.slice(5)} · aún no hay estado de cuenta de ese día`
                        : `El estado de cuenta del ${row.credit_date?.slice(5)} ya está cargado y no trae este lote`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {orphans.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-bold uppercase tracking-wide text-orange-700 mb-1">
                Líneas del banco de este día sin lote correspondiente · {fmtVES(row.orphan_total_ves)}
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg divide-y divide-orange-100">
                {orphans.map(m => (
                  <div key={m.movement_id} className="px-3 py-1.5 flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                    <span className="text-xs text-gray-500 w-12">{m.date?.slice(5)}</span>
                    <span className="text-xs text-gray-700 truncate flex-1" title={`Ref ${m.reference}`}>{m.description}</span>
                    <span className="font-mono text-sm text-gray-800 tabular-nums">{fmtVES(m.amount_ves)}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-orange-700 mt-1">
                {row.status === 'sin_lote_pos'
                  ? `El banco pagó estas líneas el ${row.credit_date?.slice(5)}, que corresponden a lotes cerrados el ${row.fecha_date?.slice(5)}. No hay ningún lote del POS ese día que cuadre con ellas. Revisar con la cajera si cerró el terminal en Tecnodroid.`
                  : 'Pueden ser liquidaciones de lotes de días anteriores (Banesco acredita ~3 días después) o lotes que la cajera no registró en el POS. Amplía el rango hacia atrás para descartar lo primero.'}
              </p>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function POSComparison() {
  const [dateFrom,       setDateFrom]       = useState(sevenDaysAgo());
  const [dateTo,         setDateTo]         = useState(today());
  const [settlementDays, setSettlementDays] = useState(3);
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [filterBanco,    setFilterBanco]    = useState('all');
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [openRows,       setOpenRows]       = useState(() => new Set());

  const rowKey = r => `${r.fecha_date}|${r.store_id}|${r.banco}`;
  const toggleRow = r => setOpenRows(prev => {
    const next = new Set(prev);
    const k = rowKey(r);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPosComparison({ dateFrom, dateTo, settlementDays });
      setData(result);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cargar la comparación.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, settlementDays]);

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  // Available banks from results
  const bancos = [...new Set(rows.map(r => r.banco))].sort();

  const filtered = rows.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterBanco !== 'all' && r.banco !== filterBanco) return false;
    return true;
  });

  // Group filtered rows by date for display
  const byDate = filtered.reduce((acc, r) => {
    if (!acc[r.fecha_date]) acc[r.fecha_date] = [];
    acc[r.fecha_date].push(r);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BanknotesIcon className="w-7 h-7 text-indigo-600" />
          Comparación Lotes POS vs Banco
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Contrasta lo que el POS reportó liquidar con los créditos reales en el estado bancario
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Desde</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Hasta</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Días de liquidación</label>
          <select
            value={settlementDays}
            onChange={e => setSettlementDays(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
          >
            {[0, 1, 2, 3, 4, 5].map(d => (
              <option key={d} value={d}>{d} {d === 3 ? '(default)' : ''}</option>
            ))}
          </select>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Consultando...' : 'Consultar'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <SummaryCard
            label="Total filas"
            value={summary.total_rows}
            sub={`${Object.keys(byDate).length} días`}
            colorClass="bg-gray-50 border-gray-200 text-gray-800"
          />
          <SummaryCard
            label="✅ Cuadra"
            value={summary.rows_ok ?? 0}
            colorClass="bg-green-50 border-green-200 text-green-800"
          />
          <SummaryCard
            label="⚠ Parcial"
            value={(summary.rows_parcial ?? 0) + (summary.rows_diferencia ?? 0)}
            colorClass="bg-yellow-50 border-yellow-200 text-yellow-800"
          />
          <SummaryCard
            label="⛔ Sin crédito"
            value={summary.rows_sin_credito ?? 0}
            sub={`el PDF ya cubre la fecha · ${summary.rows_pendiente ?? 0} pendientes de PDF`}
            colorClass="bg-gray-50 border-gray-200 text-gray-600"
          />
          <SummaryCard
            label="Banco sin POS"
            value={fmtVES(summary.orphan_total_ves)}
            sub={`${summary.orphan_lines ?? 0} líneas · ${summary.rows_sin_lote_pos ?? 0} días/banco sin lote`}
            colorClass={(summary.orphan_total_ves || 0) > 0 ? 'bg-orange-50 border-orange-200 text-orange-800' : 'bg-gray-50 border-gray-200 text-gray-600'}
          />
          <SummaryCard
            label="Diferencia total"
            value={fmtVES(summary.total_difference_ves)}
            sub={summary.total_difference_ves > 0 ? 'lotes > banco' : summary.total_difference_ves < 0 ? 'banco > lotes' : 'cuadra exacto'}
            colorClass={
              Math.abs(summary.total_difference_ves || 0) < 1
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }
          />
        </div>
      )}

      {/* Filters */}
      {data && rows.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <FunnelIcon className="w-4 h-4 text-gray-400" />
          {/* Status filter */}
          {['all', 'ok', 'parcial', 'diferencia', 'pendiente', 'sin_credito', 'sin_lote_pos'].map(s => {
            const count = s === 'all' ? rows.length : rows.filter(r => r.status === s).length;
            if (s !== 'all' && count === 0) return null;
            const labels = { all: 'Todos', ok: '✅ Cuadra', parcial: '⚠ Parcial', diferencia: '❌ Diferencia', pendiente: '🕐 Pendiente', sin_credito: '⛔ Sin crédito', sin_lote_pos: '🏦 Sin lote en POS' };
            return (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  filterStatus === s ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {labels[s]} ({count})
              </button>
            );
          })}

          {/* Bank filter */}
          {bancos.length > 1 && (
            <>
              <span className="text-gray-300">|</span>
              {['all', ...bancos].map(b => (
                <button
                  key={b}
                  onClick={() => setFilterBanco(b)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    filterBanco === b ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {b === 'all' ? 'Todos los bancos' : b.toUpperCase()}
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {/* Empty state — before search */}
      {!data && !loading && (
        <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
          <BuildingLibraryIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-500 mb-2">Selecciona un rango de fechas</h3>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            Configura el período y presiona <strong>Consultar</strong> para comparar los lotes POS
            con los créditos bancarios.
          </p>
        </div>
      )}

      {/* No results after search */}
      {data && rows.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <p className="text-gray-500">No hay lotes POS en ese período.</p>
          <p className="text-sm text-gray-400 mt-1">
            Verifica que el sync de Tecnodroid esté activo y que el período tenga datos.
          </p>
        </div>
      )}

      {/* Table */}
      {data && filtered.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {Object.entries(byDate)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([fecha, dayRows]) => (
              <div key={fecha}>
                {/* Date header */}
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">
                    {new Date(fecha + 'T12:00:00').toLocaleDateString('es-VE', {
                      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
                    })}
                  </span>
                  <span className="text-xs text-gray-400">{dayRows.length} combinaciones</span>
                </div>

                {/* Rows for this date */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left">Tienda</th>
                        <th className="px-4 py-2 text-left">Banco</th>
                        <th className="px-4 py-2 text-right">Esperado (lotes)</th>
                        <th className="px-4 py-2 text-right">Real (banco)</th>
                        <th className="px-4 py-2 text-right">Diferencia</th>
                        <th className="px-4 py-2 text-center">Estado</th>
                        <th className="px-4 py-2 text-left">Lotes conciliados</th>
                        <th className="px-4 py-2 text-left">Acreditado el</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {dayRows.sort((a, b) => a.store_name.localeCompare(b.store_name)).map((row, i) => {
                        const diffColor = row.difference_ves === 0
                          ? 'text-gray-400'
                          : row.difference_ves > 0
                            ? 'text-red-600 font-semibold'
                            : 'text-green-600 font-semibold';

                        const isOpen = openRows.has(rowKey(row));
                        return (
                          <React.Fragment key={i}>
                          <tr
                            onClick={() => toggleRow(row)}
                            className={`cursor-pointer transition-colors ${isOpen ? 'bg-indigo-50/40' : 'hover:bg-gray-50'}`}
                          >
                            <td className="px-4 py-3">
                              <span className="flex items-center gap-2">
                                <span className={`text-gray-400 text-xs transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                                <span className="font-medium text-gray-900 text-xs leading-tight block max-w-[140px] truncate">
                                  {row.store_name}
                                </span>
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <BankDot banco={row.banco} />
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-gray-800 font-semibold">
                              {row.lotes_total === 0 ? <span className="text-gray-300">—</span> : fmtVES(row.expected_ves)}
                            </td>
                            <td className={`px-4 py-3 text-right font-mono ${
                              row.actual_ves === 0 ? 'text-gray-300' : 'text-gray-800 font-semibold'
                            }`}>
                              {row.status === 'sin_lote_pos'
                                ? <span className="text-orange-700 font-semibold">{fmtVES(row.orphan_total_ves)}</span>
                                : row.actual_ves === 0 ? '—' : fmtVES(row.actual_ves)}
                            </td>
                            <td className={`px-4 py-3 text-right font-mono ${diffColor}`}>
                              {row.actual_ves === 0
                                ? '—'
                                : row.difference_ves === 0
                                  ? 'Exacto'
                                  : (row.difference_ves > 0 ? '+' : '') + fmtVES(row.difference_ves)
                              }
                              {row.difference_pct > 0 && row.actual_ves > 0 && (
                                <span className="ml-1 text-xs opacity-60">({row.difference_pct}%)</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <StatusBadge status={row.status} />
                            </td>
                            <td className="px-4 py-3">
                              <span className="flex items-center gap-2">
                                {row.lotes_total === 0 ? (
                                  <span className="text-xs text-orange-700 font-semibold">{row.orphan_movements.length} líneas del banco sin lote</span>
                                ) : (<>
                                <span className={`font-mono text-sm font-semibold ${
                                  row.lotes_matched === row.lotes_total ? 'text-green-700' : row.lotes_matched > 0 ? 'text-yellow-700' : 'text-gray-500'
                                }`}>
                                  {row.lotes_matched}/{row.lotes_total}
                                </span>
                                <span className="text-xs text-gray-400">conciliados</span>
                                </>)}
                                {row.has_wrong_bank && (
                                  <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold" title="Lotes acreditados por otro banco: el terminal está mal asignado en el POS">banco≠</span>
                                )}
                                {row.has_suspicious && (
                                  <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold" title="Hay lotes con monto sospechoso">⚠</span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400">
                              {row.matched_date
                                ? (row.matched_date === row.fecha_date ? 'Mismo día' : row.matched_date.slice(5))
                                : row.status === 'sin_lote_pos'
                                  ? <span className="text-orange-700">pagado el {row.credit_date?.slice(5)}</span>
                                  : row.status === 'pendiente'
                                    ? <span className="text-sky-700">se acredita el {row.credit_date?.slice(5)}<br /><span className="text-[10px]">falta PDF{row.last_statement ? ` (último: ${row.last_statement.slice(5)})` : ''}</span></span>
                                    : row.status === 'sin_credito'
                                      ? <span className="text-gray-500">esperado el {row.credit_date?.slice(5)}<br /><span className="text-[10px]">PDF cubre hasta {row.last_statement?.slice(5)}</span></span>
                                      : '—'
                              }
                            </td>
                          </tr>
                          {isOpen && <LineDetail row={row} />}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Settlement explanation */}
      {data && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <p className="text-xs text-blue-800">
            <strong>Ventana de liquidación: {settlementDays} días.</strong>{' '}
            Banesco y BNC se concilian por <strong>número de lote</strong> (viene en la descripción del banco; Banesco acredita ~3 días después en 2 líneas TDB+TMD).
            BDV y Banplus acreditan el <strong>neto menos 0,75% de comisión</strong>; Banplus trae el nº de lote en la referencia, BDV se cuadra por monto neto al día siguiente.
            Las líneas del banco se muestran en el día del lote al que corresponden (BDV/Banplus/BNC pagan al día siguiente, Banesco a los 3 días). "Pendiente" = todavía no hay PDF del día en que se acredita; "Sin crédito" = el PDF de ese día ya está y no trae el lote. Haz clic en una fila para ver el detalle.
          </p>
        </div>
      )}
    </div>
  );
}
